import type { ReactNode } from 'react';
import { PAGE_SIZES, STATUS_FILTERS } from '../listOptions';
import { configurationStatusLabels, type ConfigurationStatus, type PlanSort, type StatusFilter } from '../types';
import '../../job-roles/job-roles.css';
import '../benefits-deductions.css';

/** A status is always the word: the glyph is decoration (aria-hidden) and the label carries the meaning. */
export function StatusBadge({ isActive, configurationStatus }: { isActive: boolean; configurationStatus: ConfigurationStatus | null }) {
  const incomplete = configurationStatus !== null && configurationStatus !== 'Complete';
  return (
    <span className="bd-status-line">
      {isActive
        ? <span className="badge badge--approved"><span aria-hidden="true">{'✓'} </span>Active</span>
        : <span className="badge badge--neutral"><span aria-hidden="true">{'▣'} </span>Inactive</span>}
      {incomplete && (
        <span className="badge badge--warning"><span aria-hidden="true">{'⚠'} </span>{configurationStatusLabels[configurationStatus]}</span>
      )}
    </span>
  );
}

type Direction = 'ascending' | 'descending' | 'none';

/** A column header that sorts the list: a real button (keyboard works), with `aria-sort` and a visible arrow showing the current order. */
export function SortableHeader({ label, ascValue, descValue, sort, onSort }: {
  label: string;
  ascValue: PlanSort;
  descValue?: PlanSort;
  sort: PlanSort;
  onSort: (next: PlanSort) => void;
}) {
  const direction: Direction = sort === ascValue ? 'ascending' : descValue && sort === descValue ? 'descending' : 'none';
  const next = direction === 'ascending' && descValue ? descValue : ascValue;
  return (
    <th scope="col" aria-sort={direction}>
      <button type="button" className={`bd-sort${direction === 'none' ? '' : ' bd-sort--active'}`} onClick={() => onSort(next)}>
        {label}
        <svg className="bd-sort__icon" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false" data-direction={direction}>
          {direction !== 'descending' && <path d="M6 1.5L9.5 5.5h-7z" fill="currentColor" />}
          {direction !== 'ascending' && <path d="M6 10.5L2.5 6.5h7z" fill="currentColor" />}
        </svg>
      </button>
    </th>
  );
}



export function Pager({ page, pageSize, totalCount, onPage, onPageSize }: {
  page: number; pageSize: number; totalCount: number; onPage: (page: number) => void; onPageSize: (size: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  return (
    <div className="jr-pager">
      <label className="jr-pager__size">
        Rows per page{' '}
        <select value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))}>
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      <nav aria-label="Pagination" className="jr-pager__nav">
        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
        <span>Page {page} of {totalPages}</span>
        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>Next</button>
      </nav>
    </div>
  );
}

export interface FilterState {
  search: string;
  category: string;
  status: StatusFilter;
  effectiveOn: string;
}

/** Search, category, status and effective-date filters shared by the plan list and the definition list. */
export function ListFilters({ idPrefix, noun, state, categories, onChange, onClear, active }: {
  idPrefix: string;
  noun: string;
  state: FilterState;
  categories: { value: string; label: string }[];
  onChange: (patch: Partial<FilterState>) => void;
  onClear: () => void;
  active: boolean;
}): ReactNode {
  return (
    <div className="jr-toolbar">
      <div className="jr-field jr-field--search">
        <label htmlFor={`${idPrefix}-search`}>Search {noun}</label>
        <input id={`${idPrefix}-search`} type="search" placeholder="Name, short code or provider" value={state.search} onChange={(e) => onChange({ search: e.target.value })} autoComplete="off" />
      </div>
      <div className="jr-filters" role="group" aria-label="Filters">
        <div className="jr-field">
          <label htmlFor={`${idPrefix}-category`}>Category</label>
          <select id={`${idPrefix}-category`} value={state.category} onChange={(e) => onChange({ category: e.target.value })}>
            <option value="">All categories</option>
            {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div className="jr-field">
          <label htmlFor={`${idPrefix}-status`}>Status</label>
          <select id={`${idPrefix}-status`} value={state.status} onChange={(e) => onChange({ status: e.target.value as StatusFilter })}>
            {STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="jr-field">
          <label htmlFor={`${idPrefix}-effective`}>Effective on</label>
          <input id={`${idPrefix}-effective`} type="date" value={state.effectiveOn} onChange={(e) => onChange({ effectiveOn: e.target.value })} />
        </div>
        {active && <button type="button" className="link" onClick={onClear}>Clear filters</button>}
      </div>
    </div>
  );
}
